import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faUserPen, faTrash, faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Role     { id: number; name: string }
interface Partner  { name: string }
interface Marketing { name: string }

interface User {
  id         : number
  email      : string
  prepay     : boolean
  Roles?     : Role[]
  Partner?   : Partner | null
  Marketing? : Marketing | null
  Subpartner?: { email: string } | null
}

const pill = (bg: string): React.CSSProperties => ({
  display        : 'inline-flex',
  alignItems     : 'center',
  gap            : '0.35rem',
  padding        : '0.3rem 0.85rem',
  borderRadius   : 9999,
  backgroundColor: bg,
  color          : '#fff',
  fontWeight     : 700,
  fontSize       : '0.78rem',
  border         : 'none',
  cursor         : 'pointer',
  boxShadow      : `0 3px 8px ${bg}44`,
  textDecoration : 'none',
  whiteSpace     : 'nowrap' as const,
})

const Users: React.FC = () => {
  const { getHeaders }              = useContext(AuthContext) as AuthCtx
  const { t, i18n }                 = useTranslation()
  const paginationComponentOptions  = usePaginationComponentOptions()

  const [data, setData]             = useState<User[]>([])
  const [loading, setLoading]       = useState(false)
  const [totalRows, setTotalRows]   = useState(0)
  const [perPage, setPerPage]       = useState(10)
  const [email, setEmail]           = useState('')
  const [rol, setRol]               = useState(0)
  const [searchOpen, setSearchOpen] = useState(false)

  const fetchUsers = async (
    page      : number,
    perPageVal = perPage,
    emailVal   = email,
    rolVal     = rol
  ) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res = await axios.get<{ message: User[]; total: number }>(
        `${url}users?page=${page}&per_page=${perPageVal}&email=${emailVal}&rol=${rolVal}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchUsers(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchUsers(page, newPerPage)
  }

  const handleSearch = (values: { email: string; rol: number }) => {
    setEmail(values.email)
    setRol(values.rol)
    fetchUsers(1, perPage, values.email, values.rol)
  }

  const deleteUser = async (id: number) => {
    const result = await Swal.fire({
      title             : t('users.deleted'),
      text              : t('users.deleted_txt'),
      showCancelButton  : true,
      confirmButtonText : t('users.delete'),
      customClass       : { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling    : false,
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      await axios.delete(`${url}users/destroy/${id}`, { headers })
      Swal.fire({ icon: 'success', title: 'Deleted' })
      fetchUsers(1)
    } catch {
      Swal.fire({ icon: 'error', title: 'Error on delete' })
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchUsers(1) }, [i18n.language])

  const columns = [
    {
      name: t('users.email'),
      cell: (row: User) => (
        <span className="small">{row.email}</span>
      ),
    },
    {
      name: 'Roles',
      cell: (row: User) => (
        <ul className="ps-0 my-2 w-100" style={{ listStyle: 'none' }}>
          {(row.Roles ?? []).map((role, i) => (
            <li key={i} className="mb-1">
              <span
                style={{
                  display        : 'inline-block',
                  padding        : '2px 8px',
                  borderRadius   : 9999,
                  backgroundColor: '#f0fdf4',
                  color          : BRAND_DARK,
                  fontWeight     : 700,
                  fontSize       : '0.75rem',
                  border         : '1px solid #bbf7d0',
                  textTransform  : 'capitalize',
                }}
              >
                {t(`users.${role.name}`)}
              </span>
              {role.name === 'partner' && (
                <p className="mb-0 small text-muted ms-1">
                  {row.Partner?.name} · {row.prepay ? 'Pos pay' : 'Pre pay'}
                </p>
              )}
              {role.name === 'subpartner' && (
                <p className="mb-0 small text-muted ms-1">
                  {row.Partner?.name} · {row.prepay ? 'Pos pay' : 'Pre pay'}
                </p>
              )}
              {role.name === 'marketing' && (
                <p className="mb-0 small text-muted ms-1">{row.Marketing?.name}</p>
              )}
            </li>
          ))}
        </ul>
      ),
    },
    {
      name    : t('users.actions'),
      maxWidth: '220px',
      cell    : (row: User) => {
        const isAdmin = (row.Roles ?? []).some(r => r.name === 'admin')
        return (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', padding: '4px 0' }}>
            <Link to={`edit/${row.id}`} style={pill(BRAND)}>
              <FontAwesomeIcon icon={faUserPen} />
              <span>{t('users.edit')}</span>
            </Link>
            {!isAdmin && (
              <button onClick={() => deleteUser(row.id)} style={pill('#ef4444')}>
                <FontAwesomeIcon icon={faTrash} />
                <span>{t('users.delete')}</span>
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="row align-items-start">

      {/* ── Header label ─────────────────────────────────────────────── */}
      <div className="col-sm-4 col-md-2 mb-3 my-1">
        <span className="text-capitalize fw-bold">{t('users.add_user')}</span>
        <p className="text-muted small mt-1">Users &amp; roles</p>
      </div>

      {/* ── Search panel ─────────────────────────────────────────────── */}
      <div className="col-sm-8 col-md-10 mb-3">
        <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <button
            type="button"
            onClick={() => setSearchOpen(prev => !prev)}
            style={{
              width        : '100%',
              display      : 'flex',
              alignItems   : 'center',
              gap          : '0.5rem',
              padding      : '0.75rem 1.25rem',
              background   : searchOpen ? '#f8fafc' : '#fff',
              border       : 'none',
              borderBottom : searchOpen ? '1px solid #e2e8f0' : 'none',
              cursor       : 'pointer',
              fontWeight   : 700,
              fontSize     : '0.9rem',
              color        : '#475569',
              textAlign    : 'left',
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span>{t('utilities.search')}</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>

          {searchOpen && (
            <div style={{ padding: '1.25rem' }}>
              <Formik
                initialValues={{ email, rol }}
                onSubmit={handleSearch}
                enableReinitialize
              >
                <Form>
                  <div className="row align-items-center mb-3">
                    <label htmlFor="email" className="col-sm-2 col-form-label col-form-label-sm">{t('users.email')}</label>
                    <div className="col-sm-10">
                      <Field type="text" className="form-control form-control-sm" placeholder={t('users.email')} name="email" id="email" />
                    </div>
                  </div>
                  <div className="row align-items-center mb-3">
                    <label htmlFor="rol" className="col-sm-2 col-form-label col-form-label-sm">Rol</label>
                    <div className="col-sm-10">
                      <Field as="select" className="form-select form-select-sm" name="rol" id="rol">
                        <option value={0}>{t('users.all_roles')}</option>
                        <option value={1}>{t('users.admin')}</option>
                        <option value={2}>{t('users.partner')}</option>
                        <option value={3}>{t('users.subpartner')}</option>
                        <option value={4}>{t('users.marketing')}</option>
                        <option value={5}>{t('users.customer')}</option>
                        <option value={6}>{t('users.sw')}</option>
                      </Field>
                    </div>
                  </div>
                  <button className="btn btn-sm btn-bd-primary" type="submit">
                    <span className="fw-bold">{t('utilities.search_btn')}</span>
                  </button>
                </Form>
              </Formik>
            </div>
          )}
        </div>
      </div>

      {/* ── Create button ────────────────────────────────────────────── */}
      <div className="col-md-12 mb-3">
        <Link
          to="create"
          style={{
            display        : 'inline-flex',
            alignItems     : 'center',
            gap            : '0.45rem',
            padding        : '0.5rem 1.25rem',
            borderRadius   : 9999,
            background     : `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
            color          : '#fff',
            fontWeight     : 700,
            fontSize       : '0.875rem',
            textDecoration : 'none',
            boxShadow      : `0 4px 14px ${BRAND}55`,
          }}
        >
          <FontAwesomeIcon icon={faUserPlus} />
          <span>{t('users.add_user')}</span>
        </Link>
      </div>

      {/* ── DataTable ────────────────────────────────────────────────── */}
      <div className="col-md-12">
        <div className="card card-shadow">
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            fixedHeaderScrollHeight="650px"
            progressPending={loading}
            pagination
            paginationPerPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressComponent={<CustomLoader />}
            dense
          />
        </div>
      </div>

    </div>
  )
}

export default Users
